import { Children, type FocusEvent, type ReactElement } from 'react'

import type { MenuListProps, MenuProps } from 'react-select'
import ReactSelect, { components } from 'react-select'

import { ErrorMessage } from '../ErrorMessage'
import { Label } from '../Label'

import './styles.scss'

const MAX_VISIBLE_OPTIONS = 10

type Value = string | number | boolean | undefined
type ValueLabelPair<T extends Value> = {
  value: T
  label: string | JSX.Element
  isDisabled?: boolean
}

type SelectProps<T extends Value> = {
  id: string
  options: ValueLabelPair<T>[]
  value?: T
  name?: string
  label?: string | ReactElement
  ariaLabel?: string
  defaultValue?: ValueLabelPair<T>
  capitalizeLabel?: boolean
  placeholder?: string
  error?: string
  customError?: string
  touched?: boolean
  marginBottom?: number
  noBorder?: boolean
  className?: string
  isLoading?: boolean
  isClearable?: boolean
  isSearchable?: boolean
  isDisabled?: boolean
  noResultsFoundTranslation?: string | ReactElement
  hitsTranslation?: string

  onChange: (args: ValueLabelPair<T> | null) => void
  onBlur?: (e: FocusEvent<unknown, Element>) => void
}

type CustomMenuListProps = MenuListProps<any> & { hitsTranslation?: string }

export const Select = <T extends Value>({
  id,
  marginBottom,
  options,
  value,
  label,
  ariaLabel,
  capitalizeLabel,
  placeholder,
  error,
  customError,
  touched,
  name,
  defaultValue,
  className = '',
  noBorder,
  isLoading,
  isDisabled,
  isClearable = false,
  isSearchable = true,
  noResultsFoundTranslation,
  hitsTranslation,
  onChange,
  onBlur,
}: SelectProps<T>): ReactElement => {
  const currentValue =
    value !== undefined ? options.find(options => options.value === value) : defaultValue
  // todo: add wl
  const customStyles = {
    control: (baseStyles: any) => ({
      ...baseStyles,
      padding: '0.25rem 0',
      borderRadius: '0.5rem',
      ...(error && touched && { borderColor: '#e74c3c', boxShadow: '0 0 0 0.075rem #e74c3c' }),
      ...(noBorder && { border: 'none' }),
      ...(noBorder && isDisabled && { background: 'transparent' }),
    }),
    // Set the menu width to the longest option width
    menu: (base: MenuProps) => ({
      ...base,
      minWidth: '100%',
    }),
  }

  const menuComponents = hitsTranslation && {
    MenuList: (props: CustomMenuListProps) => MenuList({ ...props, hitsTranslation }),
  }

  return (
    <div className={`ui-select ${className}`} style={{ marginBottom: marginBottom || 0 }}>
      {label && (
        <Label capitalizeLabel={capitalizeLabel} htmlFor={id}>
          {label}
        </Label>
      )}

      <ReactSelect
        id={id}
        name={name}
        options={options}
        value={currentValue || null}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        components={menuComponents}
        styles={customStyles}
        isLoading={isLoading}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        onChange={onChange}
        noOptionsMessage={() => noResultsFoundTranslation}
        // Crucial for setting the "touched" fields
        onBlur={onBlur}
      />

      {error && touched && <ErrorMessage>{error}</ErrorMessage>}
      {customError && <ErrorMessage>{customError}</ErrorMessage>}
    </div>
  )
}

function MenuList(props: CustomMenuListProps) {
  const visibleChildren = Children.toArray(props.children).slice(0, MAX_VISIBLE_OPTIONS)
  const hiddenCount = Children.count(props.children) - MAX_VISIBLE_OPTIONS

  return (
    <components.MenuList {...props}>
      {visibleChildren}
      {hiddenCount > 0 && (
        <div className="px-4 py-2">
          +{hiddenCount} {props.hitsTranslation || 'hit(s)'}
        </div>
      )}
    </components.MenuList>
  )
}
